import type { Ref } from 'react';
import type { HandleId, Point } from '@os/engine';

/** Handle squares keep this size in screen pixels at every zoom level. */
export const HANDLE_PX = 8;

export type OverlayModel = {
  boxes: Point[][]; // selection outlines, screen px
  dashed?: Point[][]; // group outlines
  lines?: Point[][]; // selected connectors, along their line
  ends?: Point[]; // a selected connector's end handles
  mid?: Point | null; // and its bend handle, halfway along its line
  preview?: Point[] | null; // connector being drawn
  guides?: { a: Point; b: Point; label?: string }[]; // snapping hints (never in the document)
  hover?: { points: Point[]; line: boolean } | null; // outline of the hovered, unselected object
  dots?: { at: Point; active: boolean }[]; // anchor dots: drag one to connect
  target?: Point[] | null; // the object a connector being drawn would attach to
  targetDot?: Point | null; // and the point it would attach at
  plus?: { side: string; at: Point; active: boolean }[]; // a selected sticky note's add-a-neighbor buttons
  crop?: { full: Point[]; rect: Point[] } | null; // crop mode: whole source and draft crop
  drop?: { targets: Point[][]; label: { at: Point; text: string } | null } | null; // a move: the sections it would join, and the hint
  handles: { id: HandleId; x: number; y: number }[];
  edge?: { side: string; a: Point; b: Point } | null; // the hovered side of the selected object: dragging it resizes
  marquee: { x: number; y: number; w: number; h: number } | null;
  ghost?: { kind: string; x: number; y: number; w: number; h: number } | null; // the box being dragged out with a box tool: its shape kind, or 'box'
  tint?: string | null; // a lone selected section: its handles and hovered edge in its colour (P11 character pass)
};
export const EMPTY_OVERLAY: OverlayModel = Object.freeze({ boxes: [], handles: [], marquee: null });

const NS = 'http://www.w3.org/2000/svg';

/** Screen-space overlay, redrawn by the controller's frame loop (never by React state). Nodes are reused by key. */
export function drawOverlay(svg: SVGSVGElement, m: OverlayModel): void {
  const existing = new Map<string, SVGElement>();
  for (const node of svg.children) existing.set((node as SVGElement).dataset.k ?? '', node as SVGElement);
  const used = new Set<string>();
  const put = (key: string, tag: string, attrs: Record<string, string | number>) => {
    let node = existing.get(key);
    if (!node || node.tagName !== tag) {
      node?.remove();
      node = document.createElementNS(NS, tag);
      node.dataset.k = key;
      svg.append(node);
    }
    for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
    used.add(key);
  };
  const tint = m.tint ? `stroke: ${m.tint}` : ''; // inline, over the accent class; cleared when the tint goes
  if (m.edge) put('edge', 'line', { 'data-edge-hover': m.edge.side, x1: m.edge.a.x, y1: m.edge.a.y, x2: m.edge.b.x, y2: m.edge.b.y, class: 'stroke-accent', 'stroke-width': 2, style: tint });
  if (m.hover) {
    put('hover', m.hover.line ? 'polyline' : 'polygon', { 'data-hover': '', points: m.hover.points.map((p) => `${p.x},${p.y}`).join(' '), class: 'fill-none stroke-accent', 'stroke-width': m.hover.line ? 2 : 1.5, 'stroke-opacity': 0.6 });
  }
  m.boxes.forEach((box, i) => {
    put(`box-${i}`, 'polygon', { points: box.map((p) => `${p.x},${p.y}`).join(' '), class: 'fill-none stroke-accent', 'stroke-width': 1.5 });
  });
  (m.dashed ?? []).forEach((box, i) => {
    put(`group-${i}`, 'polygon', { points: box.map((p) => `${p.x},${p.y}`).join(' '), class: 'fill-none stroke-accent', 'stroke-width': 1.5, 'stroke-dasharray': '4 3' });
  });
  (m.lines ?? []).forEach((line, i) => {
    put(`line-${i}`, 'polyline', { points: line.map((p) => `${p.x},${p.y}`).join(' '), class: 'fill-none stroke-accent', 'stroke-width': 3, 'stroke-opacity': 0.5 });
  });
  (m.ends ?? []).forEach((p, i) => {
    put(`end-${i}`, 'circle', { 'data-end': i === 0 ? 'start' : 'end', cx: p.x, cy: p.y, r: HANDLE_PX / 2 + 1, class: 'fill-white stroke-accent', 'stroke-width': 1.5 });
  });
  if (m.mid) put('mid', 'circle', { 'data-mid': '', cx: m.mid.x, cy: m.mid.y, r: HANDLE_PX / 2, class: 'fill-accent stroke-white', 'stroke-width': 1.5 });
  if (m.preview) {
    put('preview', 'polyline', { 'data-preview': '', points: m.preview.map((p) => `${p.x},${p.y}`).join(' '), class: 'fill-none stroke-accent', 'stroke-width': 2, 'stroke-dasharray': '6 4' });
  }
  (m.guides ?? []).forEach((g, i) => {
    put(`guide-${i}`, 'line', { 'data-guide': '', x1: g.a.x, y1: g.a.y, x2: g.b.x, y2: g.b.y, class: 'stroke-[#f43f5e]', 'stroke-width': 1 });
    if (g.label) {
      put(`guide-label-${i}`, 'text', { x: (g.a.x + g.b.x) / 2, y: (g.a.y + g.b.y) / 2 - 4, 'text-anchor': 'middle', class: 'fill-[#f43f5e] font-sans text-[11px]' });
      const t = svg.querySelector(`[data-k="guide-label-${i}"]`);
      if (t && t.textContent !== g.label) t.textContent = g.label;
    }
  });
  if (m.crop) {
    const ring = (pts: Point[]) => `M${pts.map((p) => `${p.x},${p.y}`).join(' L')} Z`;
    put('crop-shade', 'path', { 'data-crop': '', d: `${ring(m.crop.full)} ${ring(m.crop.rect)}`, 'fill-rule': 'evenodd', class: 'fill-black/40 stroke-accent', 'stroke-width': 1, 'stroke-dasharray': '4 3' });
    put('crop-rect', 'polygon', { points: m.crop.rect.map((p) => `${p.x},${p.y}`).join(' '), class: 'fill-none stroke-accent', 'stroke-width': 1.5 });
  }
  (m.drop?.targets ?? []).forEach((pts, i) => {
    put(`drop-${i}`, 'polygon', { 'data-drop-target': '', points: pts.map((p) => `${p.x},${p.y}`).join(' '), class: 'fill-accent/10 stroke-accent', 'stroke-width': 2 });
  });
  const hint = m.drop?.label;
  if (hint) {
    // A chip above the dragged selection: its width follows the text once measured.
    put('drop-chip', 'rect', { x: hint.at.x, y: hint.at.y - 30, height: 22, rx: 6, class: 'fill-accent' });
    put('drop-label', 'text', { 'data-drop-label': '', x: hint.at.x + 8, y: hint.at.y - 15, class: 'fill-white font-sans text-[12px] font-medium' });
    const t = svg.querySelector<SVGTextElement>('[data-k="drop-label"]')!;
    if (t.textContent !== hint.text) {
      // Measured once per text (a new chip starts empty), not at every frame of the drag.
      t.textContent = hint.text;
      let w = hint.text.length * 7;
      try {
        w = t.getComputedTextLength?.() || w;
      } catch {
        // not laid out (tests): the estimate stays
      }
      svg.querySelector('[data-k="drop-chip"]')!.setAttribute('width', String(w + 16));
    }
  }
  if (m.target) put('target', 'polygon', { 'data-target': '', points: m.target.map((p) => `${p.x},${p.y}`).join(' '), class: 'fill-accent/5 stroke-accent', 'stroke-width': 2 });
  if (m.targetDot) put('target-dot', 'circle', { cx: m.targetDot.x, cy: m.targetDot.y, r: 5, class: 'fill-accent stroke-white', 'stroke-width': 1.5 });
  (m.dots ?? []).forEach((d, i) => {
    put(`dot-${i}`, 'circle', { 'data-dot': '', cx: d.at.x, cy: d.at.y, r: d.active ? 6 : 4.5, class: d.active ? 'fill-accent stroke-white' : 'fill-white stroke-accent', 'stroke-width': 1.5 });
  });
  for (const b of m.plus ?? []) {
    put(`plus-${b.side}`, 'circle', { 'data-plus': b.side, cx: b.at.x, cy: b.at.y, r: b.active ? 11 : 9, class: b.active ? 'fill-accent stroke-accent' : 'fill-white stroke-accent', 'stroke-width': 1.5 });
    put(`plus-${b.side}-mark`, 'path', { d: `M${b.at.x - 4},${b.at.y} h8 M${b.at.x},${b.at.y - 4} v8`, class: b.active ? 'stroke-white' : 'stroke-accent', 'stroke-width': 1.5, 'stroke-linecap': 'round' });
  }
  const half = HANDLE_PX / 2;
  for (const h of m.handles) {
    // White squares with an accent edge, whatever the interface theme: the canvas stays white (spec §14).
    const style = { 'data-handle': h.id, class: 'fill-white stroke-accent', 'stroke-width': 1.5, style: tint };
    if (h.id === 'rotate') put(`h-${h.id}`, 'circle', { ...style, cx: h.x, cy: h.y, r: half + 1 });
    else put(`h-${h.id}`, 'rect', { ...style, x: h.x - half, y: h.y - half, width: HANDLE_PX, height: HANDLE_PX, rx: 2 });
  }
  if (m.ghost) {
    // The object to be created, drawn as a dashed outline of its own shape (P5f).
    const g = m.ghost;
    const style = { 'data-ghost': g.kind, class: 'fill-accent/5 stroke-accent', 'stroke-width': 1.5, 'stroke-dasharray': '6 4' };
    if (g.kind === 'ellipse') put('ghost', 'ellipse', { ...style, cx: g.x + g.w / 2, cy: g.y + g.h / 2, rx: g.w / 2, ry: g.h / 2 });
    else if (g.kind === 'diamond')
      put('ghost', 'polygon', { ...style, points: `${g.x + g.w / 2},${g.y} ${g.x + g.w},${g.y + g.h / 2} ${g.x + g.w / 2},${g.y + g.h} ${g.x},${g.y + g.h / 2}` });
    else if (g.kind === 'triangle') put('ghost', 'polygon', { ...style, points: `${g.x + g.w / 2},${g.y} ${g.x + g.w},${g.y + g.h} ${g.x},${g.y + g.h}` });
    else put('ghost', 'rect', { ...style, x: g.x, y: g.y, width: g.w, height: g.h, rx: g.kind === 'box' ? 0 : 4 });
  }
  if (m.marquee) {
    put('marquee', 'rect', { 'data-marquee': '', x: m.marquee.x, y: m.marquee.y, width: m.marquee.w, height: m.marquee.h, class: 'fill-accent/10 stroke-accent', 'stroke-width': 1 });
  }
  for (const [key, node] of existing) if (!used.has(key)) node.remove();
}

export function Overlay({ ref }: { ref: Ref<SVGSVGElement> }) {
  return <svg ref={ref} data-layer="overlay" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full overflow-visible" />;
}
